import { useMemo, useRef } from 'react';

import { Loading } from '@/components/shared/Loading/Loading';
import { NoResults } from '@/components/shared/NoResults/NoResults';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useContentViewStore } from '@/lib/store/contentViewStore';
import { useInfiniteQuery } from '@tanstack/react-query';

import { ContentManagerService, contentKeys } from '../../services/ContentManagerService';
import BlockPagination from '../BlockList/BlockPagination';
import CollectionCard from './CollectionCard';
import CollectionFilters, { GetCollectionParams } from './CollectionFilters';

interface CollectionsListProps {
  onEdit: (slug: string) => void;
  onDelete: (slug: string) => void;
  onViewBlocks: (slug: string) => void;
}

const CollectionsList = ({ onEdit, onDelete, onViewBlocks }: CollectionsListProps) => {
  const filters = useContentViewStore((state) => state.collectionFilters);
  const setFilters = useContentViewStore((state) => state.setCollectionFilters);
  const page = useContentViewStore((state) => state.collectionPage);
  const setPage = useContentViewStore((state) => state.setCollectionPage);

  const listTopRef = useRef<HTMLDivElement>(null);
  const showPage = (index: number) => {
    setPage(index);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const debouncedTitle = useDebounce(filters.title, 300);

  const queryFilters = useMemo(
    () => ({
      ...filters,
      title: debouncedTitle,
    }),
    [filters, debouncedTitle],
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: contentKeys.collectionsPages(queryFilters),
    queryFn: ({ pageParam }) =>
      ContentManagerService.getCollections({ ...queryFilters, cursor: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.collections[lastPage.collections.length - 1]?.id : undefined,
    refetchOnWindowFocus: false,
  });

  const pages = data?.pages ?? [];
  // The remembered page outlives the cached pages, so clamp to the newest loaded one.
  const currentPage = Math.min(page, Math.max(pages.length - 1, 0));
  const collections = pages[currentPage]?.collections;
  const canGoNextPage = currentPage < pages.length - 1 || !!hasNextPage;

  const handleFilterChange = (
    key: keyof GetCollectionParams,
    value: GetCollectionParams[keyof GetCollectionParams],
  ) => {
    const normalizedValue =
      key === 'status' && Array.isArray(value) && value.length === 0 ? undefined : value;

    setFilters({ ...filters, [key]: normalizedValue });
    setPage(0);
  };

  const handleNextPage = async () => {
    const nextIndex = currentPage + 1;
    if (!pages[nextIndex]) {
      await fetchNextPage();
    }
    showPage(nextIndex);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      showPage(currentPage - 1);
    }
  };

  const renderContent = useMemo(() => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      return (
        <div className="text-center py-10 text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {error instanceof Error ? error.message : 'Failed to load collections'}
        </div>
      );
    }

    if (!collections?.length) {
      return <NoResults />;
    }

    return (
      <div className="grid gap-3">
        {collections.map((c) => (
          <CollectionCard
            key={c.slug}
            collection={c}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewBlocks={onViewBlocks}
          />
        ))}
      </div>
    );
  }, [collections, error, isLoading, onEdit, onDelete, onViewBlocks]);

  return (
    <div ref={listTopRef} className="grid gap-4">
      <CollectionFilters filters={filters} onFilterChange={handleFilterChange} />
      {renderContent}
      {(currentPage > 0 || canGoNextPage) && (
        <BlockPagination
          currentPage={currentPage}
          hasMore={canGoNextPage}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
        />
      )}
    </div>
  );
};

export default CollectionsList;
